import type { OrgChartData, OrgCycle, OrgEmployee } from "./types";

/**
 * The API returns the configured name field as the first entry in `fields`.
 * The card and search subtitle render the name next to the avatar, so they
 * should display the remaining fields only.
 */
export const getDisplayFields = (employee: OrgEmployee): OrgEmployee["fields"] => employee.fields.slice(1);

const VisitState = {
	Unvisited: 0,
	InProgress: 1,
	Completed: 2,
} as const;

type VisitState = (typeof VisitState)[keyof typeof VisitState];

export function detectCycles(employees: OrgEmployee[]): OrgCycle[] {
	const cycles: OrgCycle[] = [];
	const state = new Map<string, VisitState>();
	const parentOf = new Map<string, string | null>();
	const employeeMap = new Map<string, OrgEmployee>();

	for (const employee of employees) {
		employeeMap.set(employee.id, employee);
		state.set(employee.id, VisitState.Unvisited);
	}

	const dfs = (nodeId: string): void => {
		state.set(nodeId, VisitState.InProgress);
		const employee = employeeMap.get(nodeId);
		const managerId = employee?.managerId ?? null;

		if (managerId && employeeMap.has(managerId)) {
			const managerState = state.get(managerId);

			if (managerState === VisitState.Unvisited) {
				parentOf.set(managerId, nodeId);
				dfs(managerId);
			} else if (managerState === VisitState.InProgress) {
				const members: string[] = [];

				if (managerId === nodeId) {
					members.push(nodeId);
				} else {
					members.push(managerId);
					let current = nodeId;
					while (current !== managerId) {
						members.push(current);
						current = parentOf.get(current) ?? managerId;
					}
					members.reverse();
				}

				cycles.push({
					members,
					brokenEdge: { source: nodeId, target: managerId },
				});
			}
		}

		state.set(nodeId, VisitState.Completed);
	};

	for (const employee of employees) {
		if (state.get(employee.id) === VisitState.Unvisited) {
			dfs(employee.id);
		}
	}

	return cycles;
}

export function processOrgData(employees: OrgEmployee[]): OrgChartData {
	const cycles = detectCycles(employees);
	const cycleMemberSet = new Set<string>();
	const cycleGroups = cycles.map((cycle, index) => {
		for (const memberId of cycle.members) {
			cycleMemberSet.add(memberId);
		}
		return {
			id: `cycle-${index}`,
			memberIds: [...cycle.members],
		};
	});

	const employeeIds = new Set(employees.map((employee) => employee.id));
	const edges: Array<{ source: string; target: string }> = [];
	const roots: string[] = [];

	for (const employee of employees) {
		if (!employee.managerId || !employeeIds.has(employee.managerId)) {
			roots.push(employee.id);
			continue;
		}

		if (cycleMemberSet.has(employee.id) && cycleMemberSet.has(employee.managerId)) {
			const inSameCycle = cycleGroups.some(
				(group) => group.memberIds.includes(employee.id) && group.memberIds.includes(employee.managerId!)
			);
			if (inSameCycle) {
				roots.push(employee.id);
				continue;
			}
		}

		edges.push({ source: employee.managerId, target: employee.id });
	}

	return {
		employees,
		roots,
		cycles,
		cycleGroups,
		edges,
	};
}
